import {
  Box,
  ButtonProps,
  keyframes,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridAddIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { ReactComponent as Play } from "../../assets/icons/play.svg";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import MessageInput from "../../components/MessageInput";
import ToastNotification from "../../components/ToastNotification";
import config from "../../config.json";
import { fetchProviders } from "../../services/ProviderService";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import {
  addTestingApi,
  editTestingSessions,
  exportTesting,
  fetchTestingsById,
  getTestingById,
  IMessage,
  ITesting,
  ITestingHistory,
  Testing,
  useTestingQuery,
} from "../../services/TestingService";
import { DateRange, IField, Params } from "../../types/table/interfaces.type";
import AutoComplete from "../../components/common/AutoComplete";
import { hasJwtPermission } from "../../services/ServicesHelpers";
export interface Option {
  label: string;
  value: string;
}
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const TestingPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const ACTIVE_POLL_ID_KEY = "testing_active_poll_id";

  const [providersOptions, setProvidersOptions] = useState<Option[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<
    Params<ITesting & DateRange>
  >({
    search: "",
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [message, setMessage] = useState("");
  const [message2, setMessage2] = useState("");
  const [message3, setMessage3] = useState("");
  const [message4, setMessage4] = useState("");
  const [playingId, setPlayingId] = useState<string | number | null>(null);
  const dialogRef = useRef<TestingDialogRef>(null);
  const [rowStatuses, setRowStatuses] = useState<Record<string, string>>({});
  const pollingRef = useRef<number | null>(null);

  const checkStatus = async (id: string): Promise<boolean> => {
    try {
      const page = await fetchTestingsById(String(id));
      const item = page?.content?.[0];
      const status = item?.status as string | undefined;
      if (status) {
        setRowStatuses((prev) => ({ ...prev, [String(id)]: status }));
        if (status === "DONE" || status === "FAILED" || status === "ERROR") {
          setPlayingId(null);
          localStorage.removeItem(ACTIVE_POLL_ID_KEY);
          await queryClient.invalidateQueries({ queryKey: ["testing"] });
          return true;
        }
      }
    } catch (err) {
      console.error(
        "Error checking status for Testing ID:",
        id,
        err instanceof Error ? err.message : String(err)
      );
      setRowStatuses((prev) => ({ ...prev, [String(id)]: "ERROR" }));
    }
    return false;
  };

  const startPolling = async (id: string, options?: { resume?: boolean }) => {
    setRowStatuses((prev) => ({ ...prev, [id]: "PENDING" }));
    localStorage.setItem(ACTIVE_POLL_ID_KEY, id);

    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    if (!options?.resume) {
      try {
        await Testing(String(id));
      } catch (e) {}
    }

    const finishedImmediately = await checkStatus(id);
    if (finishedImmediately) return;
    pollingRef.current = window.setInterval(async () => {
      const finished = await checkStatus(id);
      if (finished && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }, 1000);
  };

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone = false
  ) => {
    dialogRef.current?.openDialog(mode, id, isClone);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const providerResponse = await fetchProviders({
          size: config.MAX_PAGE_SIZE,
        });
        const providersAsOptions = providerResponse.content.map((provider) => ({
          label: provider.name,
          value: provider.id,
        }));
        setProvidersOptions(providersAsOptions);
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const pendingId = localStorage.getItem(ACTIVE_POLL_ID_KEY);
    if (pendingId) {
      setPlayingId(pendingId);
      startPolling(pendingId, { resume: true });
    }
  }, []);

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
    }));
  };

  const actions = Actions({
    onCloneAction: {
      handler: async (id: string | number) => {
        console.log(`Clone action triggered for ID: ${id}`);
        handleOpenDialog("edit", String(id), true);
      },
      isDisabled: !hasJwtPermission("POST_TESTING_SESSION"),
    },
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id), false);
      },
      isDisabled: !hasJwtPermission("GET_TESTING_SESSION"),
    },

    onPlay: {
      handler: async (id: string | number) => {
        setPlayingId(id);
        console.log(`Play action triggered for ID: ${id}`);
        await startPolling(String(id));
      },
      isDisabled: (row: any) => {
        const status = rowStatuses[String(row.id)] ?? row.status;
        return status === "DONE" || status === "PENDING" || !hasJwtPermission("EXECUTE_TESTING_SESSION");
      },
      icon: (row: any) => {
        const status = rowStatuses[String(row.id)] ?? row.status;
        if (status === "PENDING") {
          return (
            <Tooltip title="Pending">
              <HourglassEmptyIcon
                sx={{
                  color: COLORS.GOLD,
                  animation: `${spin} 2s linear infinite`,
                }}
              />
            </Tooltip>
          );
        }
        if (status === "DONE") {
          return (
            <Tooltip title="Executed">
              <CheckCircleIcon
                sx={{
                  color: COLORS.GOLD,
                  animation: `${pulse} 1.5s ease-in-out`,
                }}
              />
            </Tooltip>
          );
        }
        if (status === "ERROR" || status === "FAILED") {
          return (
            <Tooltip title="Error - Click to retry">
              <Play
                style={{
                  marginTop: 3,
                  width: 23,
                  color: "#FF0000",
                }}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Execute">
            <Play
              style={{
                marginTop: 3,
                width: 23,
                color: !hasJwtPermission("EXECUTE_TESTING_SESSION") ? "#D3D3D3" : COLORS.GOLD,
              }}
            />
          </Tooltip>
        );
      },
    },
  });

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_TESTING_SESSIONS",
    },
  ];

  const fields: IField<ITesting>[] = [
    { key: "sessionName", header: "Testing Session Name" },
    { key: "createdDate", header: "Created Date" },
    { key: "executedDate", header: "Executed Date" },
    { key: "createdBy", header: "User" },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Manual Testing"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          buttons={buttons}
        />
      </Box>

      <Box mb={3}>
        <CustomLabel
          text="Testing List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />

        <SearchHeaderContainer
          search={appliedFilters.search || ""}
          setSearch={(value) =>
            setAppliedFilters((prev) => ({ ...prev, search: value }))
          }
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_TESTING_SESSION",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportTesting(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again."
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_TESTING_SESSIONS_CSV",
            },
          ]}
        />
        <CustomTable<ITesting>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useTestingQuery}
          actions={actions}
          shadeBg="#D9D9D9"
          shadeRow={(row) => {
            return Boolean(row.status === "DONE");
          }}
          sortableFields={[
            "sessionName",
            "createdDate",
            "executedDate",
            "createdBy",
          ]}
        />
      </Box>

      <TestingDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        providersOptions={providersOptions}
        setMessage={setMessage}
        message={message}
        setMessage2={setMessage2}
        message2={message2}
        setMessage3={setMessage3}
        message3={message3}
        setMessage4={setMessage4}
        message4={message4}
        appliedFilters={appliedFilters}
      />
    </Box>
  );
};

export interface TestingDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone?: boolean
  ) => void;
}

interface TestingDialogProps {
  onRefresh: () => void;
  providersOptions: Option[];
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  message: string;
  setMessage2: React.Dispatch<React.SetStateAction<string>>;
  message2: string;
  setMessage3: React.Dispatch<React.SetStateAction<string>>;
  message3: string;
  setMessage4: React.Dispatch<React.SetStateAction<string>>;
  message4: string;
  appliedFilters: Params<ITesting & DateRange>;
}

const TestingDialog = forwardRef<TestingDialogRef, TestingDialogProps>(
  (
    {
      onRefresh,
      setMessage,
      message,
      setMessage2,
      message2,
      setMessage3,
      setMessage4,
      message3,
      message4,
      appliedFilters,
      providersOptions,
    },
    ref
  ) => {
    const initialDialogData: ITesting = {
      id: "",
      sessionName: "",
      senderId: "",
      destinationNumber: "",
      vendorConnectionProfileId: "",
      messages: [
        { message: "" },
        { message: "" },
        { message: "" },
        { message: "" },
      ],
    };

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [isClone, setIsClone] = useState(false);
    const [dialogData, setDialogData] = useState<
      ITesting & {
        vendorConnectionProfileId?: string;
        destinationNumber?: string;
        senderId?: string;
      }
    >(initialDialogData);
    const [selectedId, setSelectedId] = useState<string | number>("");

    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useImperativeHandle(ref, () => ({
      openDialog: async (
        mode: "add" | "edit" | "delete" | "history",
        id?: string,
        isClone: boolean = false
      ) => {
        setSelectedId(id || "");
        setIsClone(isClone);
        setDialogMode(isClone ? "add" : mode);

        const defaultData: ITesting = { ...initialDialogData };

        if ((mode === "edit" || isClone) && id) {
          try {
            const record = await getTestingById(id);
            const newId = isClone ? "" : record.id;

            setDialogData({
              ...defaultData,
              id: newId,
              sessionName: record.sessionName,
              senderId: record.senderId,
              destinationNumber: record.destinationNumber,
              vendorConnectionProfileId: record.vendorConnectionProfileId,
              messages: record.messages.length
                ? record.messages.map((m) => ({ message: m.message || "" }))
                : [...defaultData.messages],
              status: record.status,
            });

            setMessage(record.messages[0]?.message || "");
            setMessage2(record.messages[1]?.message || "");
            setMessage3(record.messages[2]?.message || "");
            setMessage4(record.messages[3]?.message || "");
          } catch (error: any) {
            console.error("Error opening dialog:", error);
            ToastNotification.error("Failed to open the dialog.");
            return;
          }
        } else {
          setDialogData(defaultData);
          setMessage("");
          setMessage2("");
          setMessage3("");
          setMessage4("");
        }

        setDialogOpen(true);
      },
    }));

    const handleSave = async () => {
      try {
        const updatedMessages: IMessage[] = [
          {
            message,
            ...(dialogMode === "edit" && dialogData.id
              ? { testingSessionId: Number(dialogData.id) }
              : {}),
          },
          {
            message: message2,
            ...(dialogMode === "edit" && dialogData.id
              ? { testingSessionId: Number(dialogData.id) }
              : {}),
          },
          {
            message: message3,
            ...(dialogMode === "edit" && dialogData.id
              ? { testingSessionId: Number(dialogData.id) }
              : {}),
          },
          {
            message: message4,
            ...(dialogMode === "edit" && dialogData.id
              ? { testingSessionId: Number(dialogData.id) }
              : {}),
          },
        ];

        const updatedDialogData = {
          ...dialogData,
          messages: updatedMessages,
        };

        const filteredMessages = updatedDialogData.messages.filter(
          (msg) => msg.message.trim() !== ""
        );

        const dataToSave: Partial<ITesting> = {
          id: updatedDialogData.id || undefined,
          sessionName: updatedDialogData.sessionName,
          senderId: updatedDialogData.senderId,
          destinationNumber: updatedDialogData.destinationNumber,
          vendorConnectionProfileId:
            updatedDialogData.vendorConnectionProfileId,
          messages: filteredMessages,
          status: updatedDialogData.status ?? "CREATED",
        };

        if (dialogMode === "add") {
          await addTestingApi(dataToSave);
          ToastNotification.success("Testing added successfully!");
        } else {
          await editTestingSessions(dataToSave);
          ToastNotification.success("Testing updated successfully!");
        }

        setDialogOpen(false);
        setDialogData(initialDialogData);
        onRefresh();
      } catch (error: any) {
        console.error("Error saving Testing:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          `Failed to ${dialogMode === "edit" ? "update" : "add"} Testing.`;
        ToastNotification.error(errorMessage);
      }
    };

    const closeDialog = () => {
      setDialogOpen(false);
    };

    const SaveDisabled =
      !dialogData.sessionName ||
      !dialogData.senderId ||
      !dialogData.destinationNumber ||
      !dialogData.vendorConnectionProfileId;

    const fieldHistory: IField<ITestingHistory>[] = [
      {
        key: "mainId",
        header: "Testing Session Name",
        transform: (t) => t.snapshot.sessionName,
      },
      {
        key: "mainId",
        header: "Created Date",
        transform: (t) => t.snapshot.timestamp,
      },
      {
        key: "mainId",
        header: "Executed Date",
        transform: (t) => t.snapshot.executedDate,
      },
      { key: "createdBy", header: "User" },
    ];

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={closeDialog}
        title={
          dialogMode === "history"
            ? "History"
            : dialogMode === "add"
            ? isClone
              ? "Clone Testing Session"
              : "Testing Session"
            : "Edit Testing Session"
        }
        fieldsPerRow={2}
        fields={
          dialogMode === "history"
            ? [
                // {
                //   type: "custom",
                //   component: (
                //     <History
                //       filters={appliedFilters}
                //       fields={fieldHistory}
                //       fetchFunc={() => fetchTestingHistory(String(selectedId))}
                //     />
                //   ),
                // },
              ]
            : [
                {
                  type: "custom",
                  component: (
                    <MessageInput
                      placeholder="Message #1"
                      maxLength={160}
                      value={message}
                      onChange={setMessage}
                    />
                  ),
                },
                {
                  type: "text",
                  label: "Testing Session Name",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.sessionName || "",
                  validateOnChange: true,
                  onValidation: (isError) => {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      name: isError,
                    }));
                  },
                  onChange: (value) => {
                    const trimmedValue = String(value);
                    setDialogData((prev) => ({
                      ...prev,
                      sessionName: trimmedValue,
                    }));
                  },
                  placeholder: "Session name",
                  error: !!errors.name,
                },
                {
                  type: "custom",
                  component: (
                    <MessageInput
                      placeholder="Message #2"
                      maxLength={160}
                      value={message2}
                      onChange={setMessage2}
                    />
                  ),
                },
                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Provider"
                      value={dialogData.vendorConnectionProfileId ?? ""}
                      options={providersOptions}
                      onChange={(
                        value: string | number | (string | number)[]
                      ) => {
                        setDialogData((prev) => ({
                          ...prev,
                          vendorConnectionProfileId: String(value),
                        }));
                        validateField("vendorConnectionProfileId", String(value));
                      }}
                      clearable={true}
                      disablePortal={false}
                      error={!!errors.vendorConnectionProfileId}
                      placeholder="Select Provider"
                      highlightLabelStar={true}
                      helperText={
                        errors.vendorConnectionProfileId ? "Provider is required" : ""
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },
                {
                  type: "custom",
                  component: (
                    <MessageInput
                      placeholder="Message #3"
                      maxLength={160}
                      value={message3}
                      onChange={setMessage3}
                    />
                  ),
                },
                {
                  type: "text",
                  label: "Sender ID",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.senderId || "",
                  tooltip: "Accepts multiple Sender IDs separated by commas",
                  validateOnChange: true,
                  onValidation: (isError) => {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      senderId: isError,
                    }));
                  },
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      senderId: String(value),
                    }));
                  },
                  placeholder: "Sender ID",
                  error: !!errors.senderId,
                },
                {
                  type: "custom",
                  component: (
                    <MessageInput
                      placeholder="Message #4"
                      maxLength={160}
                      value={message4}
                      onChange={setMessage4}
                    />
                  ),
                },
                {
                  type: "text",
                  label: "Mobile Number",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  tooltip:
                    "Accepts multiple Mobile numbers separated by commas",
                  value: dialogData.destinationNumber || "",
                  validateOnChange: true,
                  onValidation: (isError) => {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      destinationNumber: isError,
                    }));
                  },
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      destinationNumber: String(value),
                    }));
                  },
                  placeholder: "Mobile Number",
                  error: !!errors.destinationNumber,
                },
              ]
        }
        buttons={
          dialogMode === "add" ||
          dialogData.status === "CREATED" ||
          isClone === true
            ? [
                {
                  label: "Save",
                  variant: "contained",
                  bgColor: COLORS.GOLD,
                  borderRadius: "12px",
                  onClick: handleSave,
                  disabled: SaveDisabled || (dialogMode === "edit" ? !hasJwtPermission("PUT_TESTING_SESSION") : !hasJwtPermission("POST_TESTING_SESSION")),
                },
              ]
            : []
        }
        mode={dialogMode}
      />
    );
  }
);

export default TestingPage;
